import { Platform } from 'react-native'
import AsyncStorage from '@react-native-async-storage/async-storage'
import NetInfo from '@react-native-community/netinfo'
import { apiUrl } from '../../config/config'
import { getAuthToken } from '../../utils/functions.utils'

// Sencilla cola offline para RN (no se usa en web)
// Persistimos acciones de: reaction, favourite y comment

export type OfflineAction =
  | { id: string; type: 'reaction'; postId: string; payload: { reaction_id?: string | null }; createdAt: number }
  | { id: string; type: 'favourite'; postId: string; payload: { status?: 0 | 1 }; createdAt: number }
  | { id: string; type: 'comment'; postId: string; payload: { message: string; parent_comment_id?: string | null }; createdAt: number }

const STORAGE_KEY = '@offlineQueue_v1'
let flushLock = false
let subscribed = false

async function readQueue(): Promise<OfflineAction[]> {
  try {
    const txt = await AsyncStorage.getItem(STORAGE_KEY)
    if (!txt) return []
    const arr = JSON.parse(txt)
    return Array.isArray(arr) ? arr : []
  } catch {
    return []
  }
}

async function writeQueue(queue: OfflineAction[]) {
  try { await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(queue)) } catch {}
}

export async function enqueue(action: OfflineAction) {
  if (Platform.OS === 'web') return
  const q = await readQueue()
  q.push(action)
  await writeQueue(q)
}

export async function getQueue(): Promise<OfflineAction[]> { return readQueue() }

export async function clearQueue() { await writeQueue([]) }

export function subscribeFlushOnReconnect() {
  if (Platform.OS === 'web') return
  if (subscribed) return
  subscribed = true
  NetInfo.addEventListener((state) => {
    const online = !!(state.isConnected && state.isInternetReachable)
    if (online) {
      // Intenta flush con un pequeño debounce
      setTimeout(() => { flush().catch(() => {}) }, 300)
    }
  })
}

export async function flush() {
  if (Platform.OS === 'web') return
  if (flushLock) return
  flushLock = true
  try {
    const q = await readQueue()
    if (!q.length) { flushLock = false; return }

    const token = await getAuthToken('offline_flush')
    if (!token) { flushLock = false; return }

    const headersBase: Record<string, string> = { 'Content-Type': 'application/json', Authorization: token }

    const remaining: OfflineAction[] = []
    for (const action of q) {
      try {
        if (action.type === 'reaction') {
          const h: Record<string, string> = { ...headersBase, post_id: action.postId }
          if (action.payload.reaction_id) h.reaction_id = String(action.payload.reaction_id)
          const res = await fetch(`${apiUrl}/add_like_wall`, { method: 'POST', headers: h })
          if (!res.ok) throw new Error('reaction failed')
          // no-op: el UI deberá refrescarse en siguientes fetch; la cola solo garantiza envío
        } else if (action.type === 'favourite') {
          const h: Record<string, string> = { ...headersBase, post_id: action.postId }
          const res = await fetch(`${apiUrl}/add_favourite`, { method: 'POST', headers: h })
          if (!res.ok) throw new Error('favourite failed')
        } else if (action.type === 'comment') {
          const h: Record<string, string> = { ...headersBase, post_id: action.postId }
          if (action.payload.parent_comment_id) h.parent_comment_id = String(action.payload.parent_comment_id)
          const body = JSON.stringify({ message: action.payload.message })
          const res = await fetch(`${apiUrl}/add_comment_wall`, { method: 'POST', headers: h, body })
          if (!res.ok) throw new Error('comment failed')
        }
      } catch {
        // Mantener en cola si falla (p.ej. server no disponible)
        remaining.push(action)
      }
    }
    await writeQueue(remaining)
  } finally {
    flushLock = false
  }
}

// Helper para crear acciones
export function createAction<T extends OfflineAction['type']>(type: T, postId: string, payload: any): OfflineAction {
  return {
    id: `${type}:${postId}:${Date.now()}:${Math.random().toString(36).slice(2)}`,
    type,
    postId,
    payload,
    createdAt: Date.now(),
  } as OfflineAction
}
